import React, { useState, useMemo } from 'react';
import { Package, Clock, User, CheckCircle2, XCircle, Calendar, Layers, ShieldCheck, Users, X, GitBranch } from 'lucide-react';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';

export const KpiLineDetails = ({ selectedLine, filteredWorkOrders, selectedAssetCode, onClearAsset }) => {
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'in_progress', 'completed'
    const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'corrective', 'preventive', 'predictive'

    // Extract work orders that have actually happened or are in progress (executed or ongoing)
    const executedWorkOrders = useMemo(() => {
        if (!selectedLine) return [];
        const lineData = filteredWorkOrders?.find(l => l.id === selectedLine.id || l.line === selectedLine.line) || selectedLine;
        const wos = [
            ...(lineData?.lineWorkOrders?.correctiveWorkOrders || []),
            ...(lineData?.lineWorkOrders?.predictiveWorkOrders || []),
            ...(lineData?.lineWorkOrders?.preventiveWorkOrders || [])
        ];

        // Only include work orders that have actually started or completed
        let realExecuted = wos.filter(wo => {
            const status = wo.status?.toLowerCase();
            return status === 'completed' || status === 'in_progress' || !!wo.start_date;
        });

        // Filter by selected asset if present (matches if asset code is in hierarchy or is direct target)
        if (selectedAssetCode) {
            realExecuted = realExecuted.filter(wo => {
                const hier = wo.hierarchical || [];
                const inHier = hier.includes(selectedAssetCode);
                const isDirectAsset = (wo.asset?.code === selectedAssetCode) || (wo.asset_code === selectedAssetCode);
                return inHier || isDirectAsset;
            });
        }

        // Sort descending: in_progress first, then by start_date/end_date descending
        return realExecuted.sort((a, b) => {
            if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
            if (b.status === 'in_progress' && a.status !== 'in_progress') return 1;
            const dateA = new Date(a.end_date || a.start_date || a.scheduled_date || a.created_at || 0);
            const dateB = new Date(b.end_date || b.start_date || b.scheduled_date || b.created_at || 0);
            return dateB - dateA;
        });
    }, [selectedLine, filteredWorkOrders, selectedAssetCode]);

    // Apply active filters (status & type)
    const displayWorkOrders = useMemo(() => {
        return executedWorkOrders.filter(wo => {
            const status = wo.status?.toLowerCase();
            if (statusFilter === 'completed' && status !== 'completed') return false;
            if (statusFilter === 'in_progress' && status !== 'in_progress') return false;

            const type = wo.work_order_type?.toLowerCase();
            if (typeFilter !== 'all' && type !== typeFilter) return false;

            return true;
        });
    }, [executedWorkOrders, statusFilter, typeFilter]);

    const formatDateTime = (string) => {
        if (!string) return 'N/A';
        try {
            const d = new Date(string);
            if (isNaN(d.getTime())) return string;
            return d.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return string;
        }
    };

    const getStatusBadge = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed') {
            return <Badge className="bg-emerald-950/70 text-emerald-300 border border-emerald-700/60">Completada</Badge>;
        }
        if (s === 'in_progress') {
            return <Badge className="bg-amber-950/70 text-amber-300 border border-amber-700/60 animate-pulse">En Curso</Badge>;
        }
        return <Badge className="bg-gray-800 text-gray-300 border border-gray-700">{status || 'Ejecutada'}</Badge>;
    };

    const getPriorityBadge = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'critical':
                return <Badge className="bg-red-950/60 text-red-300 border border-red-700/60">Crítica</Badge>;
            case 'high':
                return <Badge className="bg-orange-950/60 text-orange-300 border border-orange-700/60">Alta</Badge>;
            case 'medium':
                return <Badge className="bg-amber-950/60 text-amber-300 border border-amber-700/60">Media</Badge>;
            default:
                return <Badge className="bg-blue-950/60 text-blue-300 border border-blue-700/60">Baja</Badge>;
        }
    };

    const getTypeBadge = (type) => {
        switch (type?.toLowerCase()) {
            case 'corrective':
                return <Badge className="bg-rose-950/60 text-rose-300 border border-rose-800/60">Correctivo</Badge>;
            case 'preventive':
                return <Badge className="bg-lime-950/60 text-lime-300 border border-lime-800/60">Preventivo</Badge>;
            case 'predictive':
                return <Badge className="bg-cyan-950/60 text-cyan-300 border border-cyan-800/60">Predictivo</Badge>;
            default:
                return <Badge className="bg-gray-800 text-gray-300 border border-gray-700">{type || 'N/A'}</Badge>;
        }
    };

    // Empty state: No line selected
    if (!selectedLine) {
        return (
            <Card className="p-8 bg-gray-900/40 border border-gray-800 flex flex-col items-center justify-center text-center min-h-[500px] h-full rounded-2xl">
                <div className="w-16 h-16 rounded-2xl bg-gray-800/80 border border-gray-700/60 flex items-center justify-center mb-4 text-gray-500 shadow-inner">
                    <Package className="w-8 h-8 opacity-60" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Detalle de Línea de Producción</h3>
                <p className="text-sm text-gray-400 max-w-sm">
                    Selecciona una línea en el panel izquierdo para consultar el historial de intervenciones y órdenes de trabajo ejecutadas o en curso.
                </p>
            </Card>
        );
    }

    const inProgressCount = executedWorkOrders.filter(wo => wo.status === 'in_progress').length;
    const completedCount = executedWorkOrders.filter(wo => wo.status === 'completed').length;

    return (
        <Card className="p-5 bg-gray-900/50 border border-gray-800 flex flex-col rounded-2xl shadow-xl max-h-[850px] overflow-hidden">
            {/* Header: Line Information */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-800 shrink-0">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-blue-950/40 border border-blue-900/50 text-blue-400">
                        <Layers size={20} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold text-white tracking-wide">
                                Línea: {selectedLine.line}
                            </h2>
                            {selectedLine.location && (
                                <span className="text-xs text-gray-400 font-normal">
                                    • {selectedLine.location}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                            {executedWorkOrders.length} {executedWorkOrders.length === 1 ? 'intervención ejecutada / en curso' : 'intervenciones ejecutadas / en curso'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {selectedLine.successRate !== undefined && !isNaN(selectedLine.successRate) && (
                        <div className="text-right hidden sm:block">
                            <span className="text-[10px] text-gray-500 uppercase font-bold block">Tasa de Éxito</span>
                            <span className="text-sm font-bold text-emerald-400 font-mono">
                                {selectedLine.successRate.toFixed(1)}%
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Active Asset Filter Banner */}
            {selectedAssetCode && (
                <div className="flex items-center justify-between bg-blue-950/40 border border-blue-800/60 rounded-xl px-3 py-2 text-xs mt-3 shrink-0">
                    <div className="flex items-center gap-2">
                        <GitBranch size={14} className="text-blue-400 shrink-0" />
                        <span className="text-gray-400">Filtrando por activo:</span>
                        <Badge className="bg-blue-900/80 text-blue-200 border border-blue-700/60 font-mono text-xs">
                            {selectedAssetCode}
                        </Badge>
                        <span className="text-gray-400 font-medium">
                            ({executedWorkOrders.length} {executedWorkOrders.length === 1 ? 'intervención' : 'intervenciones'})
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClearAsset}
                        className="text-xs font-semibold text-blue-400 hover:text-white flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-900/40 hover:bg-blue-800/60 border border-blue-700/50 transition-colors"
                        title="Restablecer filtro a toda la línea"
                    >
                        <X size={12} /> Ver toda la línea
                    </button>
                </div>
            )}

            {/* Filter Tabs */}
            <div className="flex flex-wrap items-center justify-between gap-2 py-3 border-b border-gray-800/80 shrink-0">
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={() => setStatusFilter('all')}
                        className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap ${statusFilter === 'all'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-gray-800/60 text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                            }`}
                    >
                        Todas ({executedWorkOrders.length})
                    </button>
                    <button
                        onClick={() => setStatusFilter('in_progress')}
                        className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap ${statusFilter === 'in_progress'
                            ? 'bg-amber-600 text-white shadow-md'
                            : 'bg-gray-800/60 text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                            }`}
                    >
                        En Curso ({inProgressCount})
                    </button>
                    <button
                        onClick={() => setStatusFilter('completed')}
                        className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap ${statusFilter === 'completed'
                            ? 'bg-emerald-600 text-white shadow-md'
                            : 'bg-gray-800/60 text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                            }`}
                    >
                        Completadas ({completedCount})
                    </button>
                </div>

                {/* Type Filter dropdown / pills */}
                <div className="flex items-center gap-1">
                    {['all', 'corrective', 'preventive', 'predictive'].map(t => (
                        <button
                            key={t}
                            onClick={() => setTypeFilter(t)}
                            className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${typeFilter === t
                                ? 'bg-gray-700 text-white font-semibold'
                                : 'text-gray-500 hover:text-gray-300'
                                }`}
                        >
                            {t === 'all' ? 'Todos' : t === 'corrective' ? 'Corr.' : t === 'preventive' ? 'Prev.' : 'Pred.'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Vertical Stack of Detailed Work Order Cards */}
            {displayWorkOrders.length === 0 ? (
                <div className="py-16 text-center text-gray-500 flex flex-col items-center justify-center flex-1">
                    <Package className="w-12 h-12 mb-3 opacity-40" />
                    <p className="text-sm font-medium">No hay órdenes de trabajo ejecutadas en este filtro</p>
                </div>
            ) : (
                <div className="flex flex-col gap-4 pt-3 flex-1 min-h-0 overflow-y-auto pr-1">
                    {displayWorkOrders.map(wo => (
                        <div
                            key={wo.id}
                            className="bg-gray-800/40 border border-gray-700/70 hover:border-gray-600/90 rounded-xl p-4 flex flex-col gap-3.5 shadow-md transition-all"
                        >
                            {/* Card Header: Title & Badges */}
                            <div className="flex flex-wrap items-start justify-between gap-2 pb-2.5 border-b border-gray-700/50">
                                <div className="space-y-0.5">
                                    <div className="flex items-center gap-2">

                                        <h3 className="text-sm font-bold text-white leading-snug">
                                            {wo.title || 'Orden de Trabajo'}
                                        </h3>
                                    </div>
                                    <p className="text-xs text-gray-400">
                                        Activo: <span className="text-gray-200 font-medium">{wo.asset?.name || wo.asset_name || `ID ${wo.asset_id}` || 'N/A'}</span>
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {getStatusBadge(wo.status)}
                                    {getTypeBadge(wo.work_order_type)}
                                    {getPriorityBadge(wo.priority)}
                                </div>
                            </div>

                            {/* Details Grid (Without Tasks) */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
                                <div className="bg-gray-900/60 p-2 rounded-lg border border-gray-800/80">
                                    <span className="text-[10px] text-gray-400 uppercase font-semibold block mb-0.5">
                                        Fecha Inicio
                                    </span>
                                    <span className="text-white font-medium flex items-center gap-1">
                                        <Clock size={12} className="text-amber-400 shrink-0" />
                                        {formatDateTime(wo.start_date || wo.scheduled_date)}
                                    </span>
                                </div>

                                <div className="bg-gray-900/60 p-2 rounded-lg border border-gray-800/80">
                                    <span className="text-[10px] text-gray-400 uppercase font-semibold block mb-0.5">
                                        Fecha Fin
                                    </span>
                                    <span className="text-white font-medium flex items-center gap-1">
                                        <Clock size={12} className="text-emerald-400 shrink-0" />
                                        {formatDateTime(wo.end_date)}
                                    </span>
                                </div>

                                <div className="bg-gray-900/60 p-2 rounded-lg border border-gray-800/80">
                                    <span className="text-[10px] text-gray-400 uppercase font-semibold block mb-0.5">
                                        Horas (Est. / Real)
                                    </span>
                                    <span className="text-white font-medium font-mono">
                                        {wo.estimated_hours || 0}h / {wo.actual_hours || 0}h
                                    </span>
                                </div>

                                <div className="bg-gray-900/60 p-2 rounded-lg border border-gray-800/80">
                                    <span className="text-[10px] text-gray-400 uppercase font-semibold block mb-0.5">
                                        Creado Por
                                    </span>
                                    <span className="text-white font-medium truncate flex items-center gap-1">
                                        <User size={12} className="text-gray-400 shrink-0" />
                                        {wo.creator?.first_name ? `${wo.creator.first_name} ${wo.creator.last_name || ''}` : (wo.creator_name || 'N/A')}
                                    </span>
                                </div>
                            </div>

                            {/* Description */}
                            {wo.description && (
                                <div className="bg-gray-900/40 p-2.5 rounded-lg border border-gray-800/60 text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">
                                    <span className="text-[10px] text-gray-400 uppercase font-semibold block mb-0.5">
                                        Descripción
                                    </span>
                                    {wo.description}
                                </div>
                            )}

                            {/* Quality Check & Completion Notes (For completed WOs) */}
                            {wo.status === 'completed' && (
                                <div className="p-2.5 rounded-lg bg-gray-900/70 border border-gray-800/90 flex flex-col gap-1.5">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-gray-400 uppercase font-bold flex items-center gap-1">
                                            <ShieldCheck size={13} className="text-emerald-400" />
                                            Verificación de Calidad
                                        </span>
                                        {wo.quality_check ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-800/50">
                                                <CheckCircle2 size={12} /> Conforme / Éxito
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-400 bg-red-950/60 px-2 py-0.5 rounded border border-red-800/50">
                                                <XCircle size={12} /> Con Incidencias
                                            </span>
                                        )}
                                    </div>
                                    {wo.completion_notes && (
                                        <p className="text-xs text-gray-300 italic pt-1 border-t border-gray-800">
                                            "{wo.completion_notes}"
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};
